import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuthApiService } from '../core/auth-api.service';
import { AuthStoreService } from '../core/auth-store.service';
import { LOGIN_PASSWORD_MIN_LENGTH } from '../shared/password-policy.validators';

@Component({
  selector: 'app-login',
  templateUrl: './login.html',
  styleUrls: ['./login.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    RouterLink,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  hide = true;
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authApi = inject(AuthApiService);
  private authStore = inject(AuthStoreService);

  readonly submitting = signal(false);
  readonly errorMessage = signal('');

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(LOGIN_PASSWORD_MIN_LENGTH)]],
  });

  constructor() {
    const reason = this.route.snapshot.queryParamMap.get('reason');
    if (reason === 'admin-required') {
      this.errorMessage.set('A platform-level account is required to access the admin panel.');
    }
  }

  async submit() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set('');

    try {
      const email = this.form.controls.email.value?.trim() ?? '';
      const password = this.form.controls.password.value ?? '';
      const session = await firstValueFrom(this.authApi.login(email, password));

      if (session.user.role !== 'platform_administrator' && session.user.role !== 'admin') {
        this.errorMessage.set('This account is not allowed to access the admin panel.');
        this.authStore.clearSession();
        return;
      }

      this.authStore.setSession(session);
      await this.router.navigate(['/dashboard']);
    } catch {
      this.errorMessage.set('Sign in failed. Check your email and password.');
    } finally {
      this.submitting.set(false);
    }
  }

  toggleHide() {
    this.hide = !this.hide;
  }
}
